import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { stepCodes } from '@hooks/useUpdateStepCode';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useCheckStockCarAvailabilityCheck } from '@hooks/useStockCarAvailabilityCheck';
import { useDealerDuck } from '@hooks/useDealerDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useJourneyType } from '@hooks/useJourneyType';
import { useNavigationDuck } from '@hooks/useNavigationDuck';
import { useTranslations } from '@hooks/useTranslations';
import { useUserDuck } from '@hooks/useUserDuck';
import { useGTM } from '@hooks/useGTM';
import CheckoutPreOrder from '@components/CheckoutPreOrder';
import CheckoutStepWizard from '@components/CheckoutStepWizard';
import StockService from 'src/services/stock/stock.service';
import Main from '@components/Layout/Main';
import RedirectionModal from '@components/RedirectionModal';
import { batch, useDispatch } from 'react-redux';
import { brandIdRedirect, buildParameterizedPathname, getInitalJourneyType } from '@utils/url.utils';
import { getSessionIdCookie } from '@utils/Session.utils';
import { handleFinanceQuote, updateGeneralContext } from '@utils/getInitialProps/common';
import Path from '@utils/Path.utils';
import { DEAL_PRODUCT_TYPE, ModalVersion, PxVersion, SESSION_COOKIE_NAME } from '../../constants/main';
import {
    BUDGET_TYPE_CASH,
    BUDGET_TYPE_FINANCE,
    CarJourneyType,
    CASH,
    dealerApi,
    DealService,
    FINANCE,
    SessionInfoApi,
    UserService,
} from '../../services';
import { checkout_config } from '../../constants/checkout';
import { AxiosError } from 'axios';
import { BRANDID_CLIENT_ID, BRANDID_LOGIN_URL, BRANDID_RETURN_URL } from '../../constants/myAccount';
import { FEATURES_LIST, FeatureSwitchContext } from '../../context/featureSwitchApp';
import { MySavedDealJourneyType } from '../../redux/user/user.duck.interface';
import FilterDuck from '../../redux/filters/filter.duck';
import { IVehicleTransformed } from '../../services/stock/models/stockItems.service.model';
import routes from '../../constants/routes';
import { CashPaymentStatuses } from '../../constants/paymentStatuses';
import { CATALAN_SPAIN_CULTURE, useCatalanSwitch } from '@hooks/useCatalanSwitch';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { EUserLoginType, IUserAuth } from 'src/interfaces/User';
import { ContextWithStore } from '@utils/getInitialProps/@types';
import carDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import dealerDuck from 'src/redux/dealer/dealer.duck';
import globalDuck from 'src/redux/global/global.duck';
import navigationDuck from 'src/redux/navigation/navigation.duck';
import { NETWORK_STATUS_DONE } from 'src/redux/redux.interface';
import userDuck from 'src/redux/user/user.duck';
import { BANK_CHECKOUT_JOURNEY_TYPES } from '../../constants/enums';
import { PrePreOrderCheckoutTransitionOverlay } from '@components/PrePreOrderCheckoutTransitionOverlay';
import { validateDealerId } from '@utils/Deal.utils';
import { syncPartExchange } from '../../partExchange/service/ApiService';
import ConfiguratorDuck from '../../redux/configurator/configurator.duck';
import { processMopData } from '../../partExchange/utility/general';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';

export const journeys: any = {
    cash: 'checkoutCash',
    finance: 'checkoutFinance',
};

export const errorCodes = {
    expiredDeal: 400401,
    dealNotFound: 400404,
    sepaCompleted: 400408,
    orderPlaced: 404010,
    dealerNotFound: 400411,
    cafAlreadyLocked: 400416,
};

type IComponentProps = {};

export const dealStatuses = {
    completed: 'completed',
    checkoutDealer: 'checkout_dealer',
    offerCreated: 'offer_created',
    checkoutMyDetails: 'checkout_my_details',
    checkoutProforma: 'checkout_proforma',
    vehicleSummary: 'vehicle_summary',
    redirectedToSepa: 'redirected_to_sepa',
    redirectedToLoanProvider: 'redirected_to_loan_provider',
    sepaFailed: 'sepa_failed',
    cashPayment: 'cash_payment',
    cashPaymentKo: 'cash_payment_ko',
};

function PreOrder({}: IComponentProps) {
    const { t } = useTranslations();
    const { pushPreOrderPageLoad, pushCheckoutEvent } = useGTM();
    const router = useRouter();
    const [isValidationModalOpen, setIsValidationModalOpen] = useState<boolean>(true);

    const [transitionOverlay, setTransitionOverlay] = useState<boolean>(false);
    const [transitionOverlayError, setTransitionOverlayError] = useState<boolean>(false);

    const dispatch = useDispatch();
    const { paymentJourneyType } = useJourneyType();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { userAuth } = useUserDuck();
    const { dealer } = useDealerDuck();
    const { navigation } = useNavigationDuck();
    const catalanSwitch = useCatalanSwitch();
    const { isStockJourney } = useCarStockJourneyCheck();
    const isGigyaEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_GIGYA_ENABLED);

    const context = useContext(FeatureSwitchContext);

    const isEC41Journey = currentDeal?.productType === DEAL_PRODUCT_TYPE.CHILD;
    const defaultBankJourney =
        useContext(FeatureSwitchContext)?.FEATURE_SWITCH_BANK_CHECKOUT_DEFAULT_JOURNEY_TYPE ?? '';
    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);

    useEffect(() => {
        SessionInfoApi.updateMopStep(undefined, stepCodes.preOrder, getSessionIdCookie(), currentDeal.mopId);

        const setDealStatus = async () => {
            try {
                return await DealService.updateDeal(
                    {
                        status: dealStatuses.checkoutProforma,
                        saved: userAuth.isLoggedIn,
                        ...(defaultBankJourney && {
                            extra_field: {
                                assistedFinance: defaultBankJourney === BANK_CHECKOUT_JOURNEY_TYPES.ASSISTED,
                            },
                        }),
                    },
                    getSessionIdCookie()
                );
                // If the user comes from my account and we set the status code again to already set, then BE returns 500 error but we don't want to catch error that the status code is already set.
            } catch {}
        };

        (async () => {
            if (currentDeal.status !== dealStatuses.redirectedToLoanProvider) {
                await setDealStatus();
            }
        })();
    }, []);

    const isFinanceJourney = paymentJourneyType === BUDGET_TYPE_FINANCE;

    useCheckStockCarAvailabilityCheck(
        carDetails?.model?.title,
        carDetails?.nameplateBodyStyleSlug,
        paymentJourneyType,
        carDetails?.stockSpecificFields?.booked
    );

    const fetchRedirectUrl = useCallback(async () => {
        const financeFlow: number = isFinanceJourney ? 1 : 0;
        dispatch(UIDuck.setLoading(10));
        return DealService.getCheckoutRedirectUrl(
            isFinanceJourney ? BUDGET_TYPE_FINANCE : BUDGET_TYPE_CASH,
            financeFlow,
            getSessionIdCookie(),
            catalanSwitch.catalanSelected ? CATALAN_SPAIN_CULTURE : undefined
        )
            .then(async (res: any) => {
                const { redirectUrl, mopId, brand, countryCode, status, error } = res.data;
                setIsValidationModalOpen(false);

                if (error) {
                    dispatch(
                        UIDuck.openModal({
                            data: { data: {} },
                            modalType: UIDuck.MODAL_TYPES.ORDER_REVIEW_REDIRECT_ERROR,
                            modalVersion: ModalVersion.v2,
                            modalProperties: {
                                centerModeOnDesktop: true,
                            },
                        })
                    );
                    return false;
                }

                if (!isFinanceJourney) {
                    dispatch(
                        navigationDuck.setRedirectUrl(
                            Path.route(
                                redirectUrl,
                                {},
                                {
                                    brand: brand || checkout_config.checkoutBrand,
                                    country: countryCode || checkout_config.checkoutCountryCode.toUpperCase(),
                                    mopid: mopId,
                                }
                            ),
                            mopId
                        )
                    );
                } else {
                    dispatch(navigationDuck.setRedirectUrl(Path.route(redirectUrl, {}, {}), null));
                }

                const toBeRedirectedTo: string = Path.route(
                    redirectUrl,
                    {},
                    {
                        brand: brand || checkout_config.checkoutBrand,
                        country: countryCode || checkout_config.checkoutCountryCode.toUpperCase(),
                        mopid: mopId,
                    }
                );

                switch (status) {
                    case dealStatuses.redirectedToSepa:
                        window.location.href = toBeRedirectedTo;
                        break;
                    case dealStatuses.completed:
                        window.location.href = checkout_config.myAccountUrl + '/my_saved_deals';
                        break;
                    default:
                        break;
                }

                return redirectUrl;
            })
            .catch((err: AxiosError) => {
                if (err.response) {
                    const {
                        response: {
                            data: { code },
                        },
                    } = err;

                    switch (code) {
                        case errorCodes.expiredDeal:
                            setIsValidationModalOpen(false);
                            router.push(`/checkout/order-review`);
                            break;
                        case errorCodes.dealerNotFound:
                            setIsValidationModalOpen(false);
                            router.push(`/checkout`);
                            break;
                        case errorCodes.cafAlreadyLocked:
                            setIsValidationModalOpen(false);
                            break;
                        case errorCodes.sepaCompleted:
                        case errorCodes.orderPlaced:
                        case errorCodes.dealNotFound:
                            window.location.href = checkout_config.myAccountUrl + '/my_saved_deals';
                            break;
                        default:
                            setIsValidationModalOpen(false);
                            break;
                    }
                }
                if (isFinanceJourney) {
                    setIsValidationModalOpen(false);
                    setTransitionOverlayError(true);
                }
            })
            .finally(() => {
                dispatch(UIDuck.setLoading(-10));
            });
    }, [dispatch, isFinanceJourney, navigation.journey, router, catalanSwitch.catalanSelected]);

    const isCheckoutPaymentMockEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CHECKOUT_PAYMENT_MOCK_ENABLED
    );

    const handleStockCarAvailabilityError = (vehicle: IVehicleTransformed) => {
        const { model, bodyStyle, nameplateBodyStyleSlug } = vehicle;
        const modelUrl = buildParameterizedPathname(routes.SELECTOR, CarJourneyType.STOCK, {
            nameplateBodyStyleSlug: nameplateBodyStyleSlug,
            modelId: model.id,
            bodyStyleId: bodyStyle.id,
        });

        dispatch(
            UIDuck.openModal({
                data: {
                    modelName: model.title,
                    modelUrl: modelUrl,
                },
                modalType: UIDuck.MODAL_TYPES.STOCK_CAR_UNAVAILABLE,
                modalVersion: ModalVersion.v2,
                modalProperties: {
                    centerModeOnDesktop: true,
                },
            })
        );
    };

    const sleep = async () => await new Promise((resolve) => setTimeout(resolve, 5000));

    const handleRedirect = useCallback(async () => {
        setTransitionOverlay(true);
        setTransitionOverlayError(false);
        await sleep();
        dispatch(UIDuck.setLoading(10));
        let redirectUrl: string;

        // check availability of vehicle
        if (carDetails.stock) {
            const { available } = await StockService.checkVehicleAvailability(carDetails.orders[0].uuid);

            if (!available) {
                return handleStockCarAvailabilityError(carDetails);
            }
        }

        //fetch redirect url after button is clicked, it used to be on pageLoad
        try {
            redirectUrl = await fetchRedirectUrl();
        } catch (err: any) {
            setTransitionOverlayError(true);
            console.error(err);
        }

        // Route set for testing purposes
        if (isCheckoutPaymentMockEnabled) {
            dispatch(UIDuck.setLoading(-10));
            return router.push('/checkout/order-review');
        } else {
            let { redirectedToSepa: status } = dealStatuses;
            let openValModal: boolean = true;

            if (isFinanceJourney) {
                status = dealStatuses.redirectedToLoanProvider;
                openValModal = false;
            }

            const isCashJourney = currentDeal.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH;
            const mopStatus = isCashJourney ? undefined : status;
            const mopStepCode = isCashJourney ? stepCodes.cashPayment : undefined;

            setIsValidationModalOpen(openValModal);
            try {
                if (isFinanceJourney) {
                    await SessionInfoApi.updateMopStep(
                        undefined,
                        isAssistedFinance ? stepCodes.financePending : stepCodes.bank,
                        getSessionIdCookie(),
                        currentDeal.mopId
                    );
                }
            } catch (e: any) {
                console.log(e);
                setTransitionOverlayError(true);
                setIsValidationModalOpen(false);
            }

            dispatch(UIDuck.setLoading(-10));

            if (redirectUrl?.includes('http')) {
                window.location.href = redirectUrl;
            } else if (redirectUrl) {
                return router.push(redirectUrl);
            }
        }
    }, [navigation, router, isCheckoutPaymentMockEnabled, fetchRedirectUrl]);

    const redirectIfNotLogged = (userAuth: IUserAuth) => {
        // APP-21428
        if (userAuth.loginType === EUserLoginType.GUEST) {
            return;
        }
        if (!userAuth.isLoggedIn && router?.query?.dealId) {
            const brandIdStateJson: string = JSON.stringify({
                redirectTo: window.location.href,
            });

            const url: string = BRANDID_LOGIN_URL.replace('{clientId}', BRANDID_CLIENT_ID).replace(
                '{redirectURL}',
                BRANDID_RETURN_URL
            );
            brandIdRedirect(url, brandIdStateJson, {
                isLogin: true,
                isGigyaEnabled,
            });
        }
        if (Object.entries(carDetails).length === 0) {
            return <RedirectionModal />;
        }
    };

    useEffect(() => {
        if (userAuth.status === NETWORK_STATUS_DONE && dealer?.name && Object.entries(carDetails).length !== 0) {
            pushPreOrderPageLoad(carDetails, dealer, paymentJourneyType, isEC41Journey);
            pushCheckoutEvent(3, carDetails);
        }
    }, [userAuth.status, dealer]);

    useEffect(() => {
        if (currentDeal?.partExchangeId) {
            console.log('PX VERSION', pxVersion);

            if (pxVersion === PxVersion.v2 && currentDeal?.partExchangeId) {
                syncPartExchange(currentDeal?.token).then(({ data }) => {
                    console.log('PX DATA', data);
                    dispatch(ConfiguratorDuck.setPxState(processMopData(data?.data)));
                });
            }
        }
    }, [currentDeal]);

    useEffect(() => {
        if (userAuth.status === NETWORK_STATUS_DONE) {
            window.scrollTo(0, 0);
            redirectIfNotLogged(userAuth);
        }
    }, [userAuth.status]);

    //TODO remove, divide into smaller components, only componente renderer exclusively on client can use useLayoutEffect
    useLayoutEffect(() => {
        if (!isStockJourney) {
            DealService.currentDeal(getSessionIdCookie()).then((res) => {
                if (validateDealerId(res.data.dealerId)) {
                    dealerApi.getDealer(res.data.dealerId).then((res) => dispatch(dealerDuck.setDealer(res.data)));
                }
            });
        }

        // dispatch(navigationDuck.loadSteps(navigation.journey, 'my-details', `preOrder`));
        return () => {
            setIsValidationModalOpen(false);
        };
    }, []);

    const isAssistedFinance = useMemo(() => currentDeal?.extraFields?.assistedFinance, [currentDeal]);

    return (
        <Main title={t('checkout.tabTitle')} checkout={!transitionOverlay}>
            {isFinanceJourney ? (
                <>
                    {!transitionOverlay ? (
                        <>
                            <CheckoutStepWizard actualState="pre-order" stock={carDetails.stock} />
                            <CheckoutPreOrder
                                handleRedirect={handleRedirect}
                                openValidationModal={isValidationModalOpen}
                                {...catalanSwitch}
                            />
                        </>
                    ) : (
                        <PrePreOrderCheckoutTransitionOverlay
                            handleRedirect={handleRedirect}
                            isAssistedFinance={isAssistedFinance}
                            transitionOverlayError={transitionOverlayError}
                        />
                    )}
                </>
            ) : (
                <>
                    <CheckoutStepWizard actualState="pre-order" stock={carDetails.stock} />
                    <CheckoutPreOrder
                        handleRedirect={handleRedirect}
                        openValidationModal={isValidationModalOpen}
                        {...catalanSwitch}
                    />
                </>
            )}
        </Main>
    );
}

PreOrder.getInitialProps = async (ctx: ContextWithStore) => {
    const { store, query, req, res, featureContext } = await updateGeneralContext(ctx);

    try {
        const token = req?.cookies?.[SESSION_COOKIE_NAME] ?? getSessionIdCookie();
        if (query?.dealId) {
            store.dispatch(carDetailsDuck.setCurrentDeal(null));
            store.dispatch(carDetailsDuck.setCurrentCarDetails(null));
            const activateRes = await DealService.activateDeal(query.dealId, token);
            const cookieHeader = activateRes?.headers?.['set-cookie'];

            if (cookieHeader && res?.setHeader) {
                res.setHeader('set-cookie', cookieHeader);
            }
        }

        if (
            !carDetailsDuck.getCurrentDeal(store.getState())?.token ||
            !carDetailsDuck.getCurrentCarDetails(store.getState())?.lcdv16
        ) {
            const currentDeal = await DealService.currentDeal(token);

            const carJourney = getInitalJourneyType(currentDeal.data.fullProductConfiguration);

            if (currentDeal.data.journeyType === MySavedDealJourneyType.BUY_ONLINE_FINANCE) {
                await handleFinanceQuote(store, currentDeal?.data?.financeQuoteId);
            }

            const userAddress = await UserService.userAdress(token);
            const userDetails = await UserService.userDetails(token);
            const carConfiguration = currentDeal.data.fullProductConfiguration;
            const dealer =
                carConfiguration.stock || !!carConfiguration.dealers?.length
                    ? carConfiguration.dealers[0]
                    : (await dealerApi.getDealer(currentDeal.data.dealerId))?.data;

            batch(() => {
                store.dispatch(carDetailsDuck.setCurrentDeal(currentDeal.data));
                store.dispatch(carDetailsDuck.setCurrentCarDetails(carConfiguration));
                store.dispatch(dealerDuck.setDealer(dealer));
                store.dispatch(globalDuck.setCarJourney(carJourney));
                store.dispatch(userDuck.setUserAddress(userAddress.data.address));
                store.dispatch(userDuck.setUserDetails(userDetails.data));
                store.dispatch(
                    FilterDuck.changeBudgetType(
                        currentDeal.data.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH ? CASH : FINANCE
                    )
                );
            });

            if (
                currentDeal.data.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH &&
                query.status !== CashPaymentStatuses.accepted
            ) {
                await SessionInfoApi.updateMopStep(
                    undefined,
                    stepCodes.cashPaymentKo,
                    getSessionIdCookie(),
                    currentDeal.data.mopId
                );
            }
        } else if (!dealerDuck.getOwnState(store.getState()).dealer?.name) {
            const currentDeal = carDetailsDuck.getCurrentDeal(store.getState());
            const carConfiguration = carDetailsDuck.getCurrentCarDetails(store.getState());
            const dealer =
                carConfiguration.stock || !!carConfiguration.dealers?.length
                    ? carConfiguration.dealers[0]
                    : (await dealerApi.getDealer(currentDeal.dealerId))?.data;

            store.dispatch(dealerDuck.setDealer(dealer));
        }
    } catch (e: any) {
        console.error(e);
    }
    return { query, namespacesRequired: ['common'] };
};

export default PreOrder;
